import apiClient from "@/api/client";
import type {
  AsyncTaskRecord,
  QuantitySummary,
  ZEDrawingFeatureCollection,
  ZEElementType,
  ZETender,
  ZETenderParcel,
  ZETenderParcelDraft,
  ZETenderRoadNeighbor,
  ZETenderVariant,
} from "./types";

// apiClient defaults to application/json; FormData posts must override it or DRF
// rejects the multipart body with 415 Unsupported Media Type.
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } } as const;

// ZE-tender routes are mounted under the same DLC router as power-projects.
const BASE = "/power-projects";

async function unwrap<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  const r = await p;
  return r.data.data;
}

// ── geoportal deep link ───────────────────────────────────────────────────────
// Reproject a WGS84 map extent → PUWG 1992 (EPSG:2180) server-side (no proj4 in FE) and get
// back a ready-to-open link into the NEW Geoportal (imapnext: centre + scale, `mapview`).
// width_px/height_px = this map's viewport, so the chosen scale fits the whole extent.
export const getGeoportalUrl = (b: {
  sw_lng: number;
  sw_lat: number;
  ne_lng: number;
  ne_lat: number;
  width_px?: number;
  height_px?: number;
}) =>
  unwrap<{ bbox: string; mapview: string; url: string }>(
    apiClient.get(`/geoportal-bbox/`, { params: b }),
  );

// ── address geocoding (GUGiK Universal Geocoder, reprojected to WGS84 server-side) ─
export interface ZEGeocodeResult {
  lat: number;
  lng: number;
  /** Best-effort formatted address echoed back so the user can confirm the match. */
  label: string;
  /** UUG match confidence 0..1 (results are ordered best-first). */
  accuracy: number;
}
export const geocodeAddress = (address: string) =>
  unwrap<{ results: ZEGeocodeResult[] }>(
    apiClient.get(`${BASE}/ze-geocode/`, { params: { address } }),
  );

// ── tenders ──────────────────────────────────────────────────────────────────
// No page_size param → OptInPageNumberPagination returns the FULL list (like
// regular tenders). Sending page_size opts into pagination, which DRF caps at
// MAX_PAGE_SIZE (500), silently truncating the list.
// ?omit=description drops the one heavy column from every row: the portfolio never
// renders it (ZETender doesn't even declare it), but on an unpaginated list it was the
// bulk of the payload. Deny-list rather than ?view=compact — compact would also strip
// action_status/repeat_match/offer, which the portfolio DOES use — and rather than
// dropping the field server-side, which would change the response for other API clients.
export const getZETenders = () =>
  unwrap<ZETender[]>(apiClient.get(`${BASE}/ze-tenders/`, { params: { omit: "description" } }));

export interface ZETenderActionCounts {
  needs_pricing: number;
  pricing_claimed: number;
  needs_approval: number;
  needs_offer: number;
  offer_submitted: number;
  needs_result: number;
}

export const getZETenderActionCounts = () =>
  unwrap<ZETenderActionCounts>(apiClient.get(`${BASE}/ze-tenders/action-counts/`));

export const getZETender = (id: string) =>
  unwrap<ZETender>(apiClient.get(`${BASE}/ze-tenders/${id}/`));

export const createZETender = (payload: {
  title: string;
  client: string;
  offer_deadline: string;
  reference_number?: string;
  operator_name?: string;
}) => unwrap<ZETender>(apiClient.post(`${BASE}/ze-tenders/`, payload));

export const updateZETender = (id: string, payload: Partial<ZETender>) =>
  unwrap<ZETender>(apiClient.patch(`${BASE}/ze-tenders/${id}/`, payload));

// ── element-type catalog ─────────────────────────────────────────────────────
export const getZEElementTypes = () =>
  unwrap<ZEElementType[]>(
    apiClient.get(`${BASE}/ze-element-types/`, { params: { page_size: 1000, is_active: true } }),
  );

// ── variants + drawing ───────────────────────────────────────────────────────
export const getZEVariants = (zeTender: string) =>
  unwrap<ZETenderVariant[]>(
    apiClient.get(`${BASE}/ze-tender-variants/`, { params: { ze_tender: zeTender, page_size: 1000 } }),
  );

export const createZEVariant = (zeTender: string, label: string, note = "") =>
  unwrap<ZETenderVariant>(
    apiClient.post(`${BASE}/ze-tender-variants/`, { ze_tender: zeTender, label, note }),
  );

export const deleteZEVariant = (id: string) =>
  apiClient.delete(`${BASE}/ze-tender-variants/${id}/`);

export const patchZEVariant = (
  id: string,
  payload: Partial<Pick<ZETenderVariant, "label" | "note" | "takeoff_defaults">>,
) =>
  unwrap<ZETenderVariant>(
    apiClient.patch(`${BASE}/ze-tender-variants/${id}/`, payload),
  );

export const getVariantDrawing = (variantId: string) =>
  unwrap<ZEDrawingFeatureCollection>(apiClient.get(`${BASE}/ze-tender-variants/${variantId}/drawing/`));

export const saveDrawing = (variantId: string, fc: ZEDrawingFeatureCollection) =>
  unwrap<{ lines: number; objects: number; total_length_m: number }>(
    apiClient.post(`${BASE}/ze-tender-variants/${variantId}/save-drawing/`, fc),
  );

export const recomputeParcels = (variantId: string) =>
  unwrap<{
    task_id: string;
    mpzp_task_id: string;
    protected_areas_task_id: string;
    water_crossings_task_id: string;
  }>(
    apiClient.post(`${BASE}/ze-tender-variants/${variantId}/recompute-parcels/`, {}),
  );

export const getQuantitySummary = (variantId: string) =>
  unwrap<QuantitySummary>(apiClient.get(`${BASE}/ze-tender-variants/${variantId}/quantity-summary/`));

// Neighbours of parcels marked "WPW Prywatny — droga wewn." (ilosc_dz_prv_osc_dr_wewn*
// keys). The cheap recompute refreshes only them — run it after marking a NEW road;
// unmarking needs no task (quantity-summary filters by the current road set).
export const recomputeRoadNeighbors = (variantId: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/ze-tender-variants/${variantId}/recompute-road-neighbors/`, {}),
  );

export const getRoadNeighbors = (variantId: string) =>
  unwrap<ZETenderRoadNeighbor[]>(
    apiClient.get(`${BASE}/ze-tender-variants/${variantId}/road-neighbors/`),
  );

// DXF export of the variant's drawing. Fetched as a blob through the authenticated
// client so the JWT header is sent (a plain <a href> can't carry it). `presetId` re-layers
// the export onto that preset's layer names (matched by object type); omitted → tender layers.
// `srid` picks the output CRS: 2180 (PUWG 1992, native) or a PUWG 2000 zone 2176–2179.
export const downloadVariantDxf = async (
  variantId: string,
  presetId?: string,
  srid?: number,
): Promise<Blob> => {
  const r = await apiClient.get(`${BASE}/ze-tender-variants/${variantId}/dxf/`, {
    responseType: "blob",
    params: {
      ...(presetId ? { preset: presetId } : {}),
      ...(srid && srid !== 2180 ? { srid } : {}),
    },
  });
  return r.data as Blob;
};

// Layer presets available for a re-layered DXF download of this variant, each with a
// per-variant mapping report (which drawn types land on which layer; which have none).
export interface ZEExportLayerType {
  code: string;
  label: string;
  kind: string;
  layer?: string;
}
export interface ZEExportLayerPreset {
  id: string;
  name: string;
  description: string;
  mapped: ZEExportLayerType[];
  unmapped: ZEExportLayerType[];
}
export const getVariantLayerPresets = (variantId: string) =>
  unwrap<ZEExportLayerPreset[]>(
    apiClient.get(`${BASE}/ze-tender-variants/${variantId}/layer-presets/`),
  );

// ── parcels ──────────────────────────────────────────────────────────────────
export const getZETenderParcels = (variant: string) =>
  unwrap<ZETenderParcel[]>(
    apiClient.get(`${BASE}/ze-tender-parcels/`, { params: { variant, page_size: 1000 } }),
  );

// ── ULDK geometry fallback (for obreb TERYTs without stored geometry) ────────
export const getUldkParcelGeom = (teryt: string) =>
  unwrap<{ geometry: import("./types").GeoJsonGeometry } | null>(
    apiClient.get(`${BASE}/ze-uldk-geom/`, { params: { teryt } }),
  );

// ── parcel drafts (ownership overrides + WPW counts) ─────────────────────────
export const getZETenderParcelDrafts = (variantId: string) =>
  unwrap<ZETenderParcelDraft[]>(
    apiClient.get(`${BASE}/ze-tender-parcel-drafts/`, { params: { variant: variantId, page_size: 1000 } }),
  );

export const saveZETenderParcelDraft = (
  variantId: string,
  teryt: string,
  data: { ownership_override?: string; wpw_count?: number | null },
) =>
  unwrap<ZETenderParcelDraft>(
    apiClient.post(`${BASE}/ze-tender-parcel-drafts/`, { variant: variantId, teryt, ...data }),
  );

// ── map annotations ──────────────────────────────────────────────────────────
export const getZEMapAnnotations = (variantId: string) =>
  unwrap<import("./types").ZEMapAnnotation[]>(
    apiClient.get(`${BASE}/ze-map-annotations/`, { params: { variant: variantId, page_size: 1000 } }),
  );

export const createZEMapAnnotation = (payload: {
  variant: string;
  text: string;
  geometry: { type: string; coordinates: [number, number] };
}) =>
  unwrap<import("./types").ZEMapAnnotation>(
    apiClient.post(`${BASE}/ze-map-annotations/`, payload),
  );

export const updateZEMapAnnotation = (
  id: string,
  payload: { text?: string; geometry?: { type: string; coordinates: [number, number] } },
) =>
  unwrap<import("./types").ZEMapAnnotation>(
    apiClient.patch(`${BASE}/ze-map-annotations/${id}/`, payload),
  );

export const deleteZEMapAnnotation = (id: string) =>
  apiClient.delete(`${BASE}/ze-map-annotations/${id}/`);

// ── clients (for the create dialog) ──────────────────────────────────────────
export const getClients = () =>
  unwrap<{ id: string; name: string }[]>(apiClient.get(`/clients/`, { params: { page_size: 1000 } }));

// ── bg task polling ──────────────────────────────────────────────────────────
export const getTask = (id: string) =>
  unwrap<AsyncTaskRecord>(apiClient.get(`/bg-tasks/${id}/`));

export const cancelTask = (id: string) =>
  unwrap<AsyncTaskRecord>(apiClient.post(`/bg-tasks/${id}/cancel/`, {}));

// ── EML import ───────────────────────────────────────────────────────────────
export const startEmlImport = (emlFile: File) => {
  const fd = new FormData();
  fd.append("eml_file", emlFile);
  return unwrap<{ task_id: string }>(apiClient.post(`${BASE}/eml-import/start/`, fd, MULTIPART));
};

export const confirmEmlImport = (payload: {
  title: string;
  client: string;
  offer_deadline: string;
  operator_name?: string;
  reference_number?: string;
  rfx_row: object;
  parcels?: object[];
  signals?: object;
  confidence?: string;
}) =>
  unwrap<{ tender_id: string; tender: import("./types").ZETender }>(
    apiClient.post(`${BASE}/eml-import/confirm/`, payload),
  );

// Re-run the AI analysis for a single RfX and merge it back into the import task's
// stored result. Returns the re-analysis task id to poll; when it finishes, re-poll the
// parent import task to read the refreshed result for that RfX.
export const reanalyzeEmlRfx = (taskId: string, rfx: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/eml-import/reanalyze/`, { task_id: taskId, rfx }),
  );

export const checkEmlDuplicate = (title: string, sourceUrl?: string) =>
  unwrap<{ is_duplicate: boolean; tender_id: string | null }>(
    apiClient.get(`${BASE}/eml-import/check-duplicate/`, {
      params: { title, source_url: sourceUrl ?? "" },
    }),
  );

export const getZETenderAiAnalysis = (tenderId: string) =>
  unwrap<import("./types").ZETenderAiAnalysis | null>(
    apiClient.get(`${BASE}/ze-tenders/${tenderId}/ai-analysis/`),
  );

export const reanalyzeZETender = (tenderId: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/ze-tenders/${tenderId}/reanalyze/`, {}),
  );

export const getZECapabilities = () =>
  unwrap<import("./types").ZECapabilities>(apiClient.get(`${BASE}/ze-capabilities/`));

export const patchZECapabilities = (payload: Partial<import("./types").ZECapabilities>) =>
  unwrap<import("./types").ZECapabilities>(apiClient.patch(`${BASE}/ze-capabilities/`, payload));

export const getEnergaBranches = () =>
  unwrap<{ client: string | null; branches: { name: string; city: string; display_name: string }[] }>(
    apiClient.get(`${BASE}/ze-energa-branches/`),
  );

// ── powtórki anulowanych przetargów ──────────────────────────────────────────
// Lista zawiera też dopasowania odrzucone i te, które AI uznało za inne postępowanie —
// baner ich nie pokazuje, ale „dlaczego to zniknęło?" musi mieć odpowiedź.
export const getRepeatMatches = (tenderId: string) =>
  unwrap<import("./types").ZETenderRepeatMatch[]>(
    apiClient.get(`${BASE}/ze-tenders/${tenderId}/repeat-matches/`),
  );

export const reviewRepeatMatch = (
  tenderId: string,
  matchId: string,
  status: import("./types").ZERepeatStatus,
) =>
  unwrap<import("./types").ZETenderRepeatMatch>(
    apiClient.patch(`${BASE}/ze-tenders/${tenderId}/repeat-matches/${matchId}/`, { status }),
  );

/** Zaciąga trasę (jako nowy wariant) i/lub wycenę (jako propozycję do akceptacji)
 *  z anulowanego przetargu. `variant` = który wariant źródłowy skopiować. */
export const carryOverRepeatMatch = (
  tenderId: string,
  matchId: string,
  payload: { route?: boolean; pricing?: boolean; variant?: string },
) =>
  unwrap<import("./types").ZECarryOverResult>(
    apiClient.post(
      `${BASE}/ze-tenders/${tenderId}/repeat-matches/${matchId}/carry-over/`,
      payload,
    ),
  );

/** Ponowne wyszukanie powtórek (płatne sprawdzenie AI) — dyrektor/CEO. */
export const detectRepeats = (tenderId: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/ze-tenders/${tenderId}/detect-repeats/`, {}),
  );
